// WF-FIX (2026-10-26; UX pass 0.7): the DE's side of "DEs on the channel" — where it listens.
// - `DeChannelsSection` (DE › Overview): the channels the DE is a member of (`GET channels?
//   member_id=`, every page, only the ones the viewer can see), each a link with the channel's
//   agent wake rule as a small label and its home channel marked (the job's `home_channel_id`,
//   which the wizard's channels step writes beside the same memberships); a visible hover
//   "Remove from channel" where the viewer manages that channel (the row's `can_manage`; never
//   a DM); and "Add to channel". A failed read says so with Retry.
// - `AddToChannelButton`: the picker of the channels the viewer may add members to (the list
//   rows' `can_invite`; no DMs, agent rooms or archived ones) the DE is not in yet, which calls
//   `channels.addMember`; disabled with the reason for a paused DE. The profile header uses it.
// With the `chat` add-on off it says so in one line and makes no chat call. No sample rows.
import { Badge, Button, InfoTip, SettingsSection, toast } from "@companyos/ui";
import {
  infiniteQueryOptions,
  useInfiniteQuery,
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { HashIcon, LockIcon, MessagesSquareIcon, PlusIcon, Trash2Icon } from "lucide-react";
import { useState } from "react";
import type { Api } from "../../api/client";
import type { DeProfile } from "../../api/types";
import { keepFocus } from "../../focus/focus";
import { SearchPicker } from "../chat/channel-agents";
import { WAKE_CHIP } from "../chat/channel-settings";
import { channelsQuery, chatKeys, rulesQuery, type SidebarChannel } from "../chat/chat-queries";
import type { CompanyAccess } from "../company";
import { problemText } from "../ops-parts";
import { useEveryPage } from "../use-every-page";
import { useFeature, useSession } from "../use-feature";
import { jobQuery } from "./add-de/model";

type Props = { api: Api; company: CompanyAccess; de: DeProfile };

/** The channels a membership is in (under the chat channels key: their pings refetch it). */
const deChannelsQuery = (api: Api, co: string, membershipId: string) =>
  infiniteQueryOptions({
    queryKey: [...chatKeys.channels(co), "member", membershipId] as const,
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) =>
      api.client.channels.list({
        params: { co },
        query: { limit: 200, member_id: membershipId, ...(pageParam ? { cursor: pageParam } : {}) },
      }),
    getNextPageParam: (last) => last.next_cursor ?? undefined,
  });

/**
 * Every page of the DE's channels: `rows` null until all are in (or when Chat is off); a failed
 * page stops the reading (`failed`, `retry`) instead of asking again forever.
 */
function useDeChannels(api: Api, co: string, membershipId: string, on: boolean) {
  const q = useInfiniteQuery({ ...deChannelsQuery(api, co, membershipId), enabled: on });
  const every = useEveryPage(q);
  const rows = q.data?.pages.flatMap((p) => p.data) ?? [];
  return {
    rows: on && q.isSuccess && every.complete ? rows : null,
    failed: on && every.failed,
    retry: every.retry,
  };
}

/** Whether the viewer may write in Chat here (a membership, Chat not read-only). */
function useChatWriter(company: CompanyAccess): boolean {
  const chat = useFeature("chat");
  const { me } = useSession();
  const meId = me.memberships.find((m) => m.company_id === company.id)?.membership_id ?? null;
  return chat.enabled && !chat.readOnly && meId !== null;
}

/** Refreshes the DE's channels, the channel lists and the channel's member rows. */
function useRefresh(co: string) {
  const queryClient = useQueryClient();
  return (channelId: string) =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: chatKeys.channels(co) }),
      queryClient.invalidateQueries({ queryKey: chatKeys.members(co, channelId) }),
      queryClient.invalidateQueries({ queryKey: ["company", co, "agent-readiness"] }),
    ]);
}

const label = (c: Pick<SidebarChannel, "name">) => `#${c.name}`;

/** "Add to channel": the viewer's channels the DE is not in; picking one adds it. */
export function AddToChannelButton({
  api,
  company,
  de,
  lazy = false,
}: Props & {
  /** Load the channels only once the picker opens (the profile header: no calls per tab). */
  lazy?: boolean;
}) {
  const co = company.id;
  const writer = useChatWriter(company);
  // WF-FIX (2026-10-26): only an active membership joins a channel (the API's `activeMembers`):
  // a paused DE gets the button disabled with the reason, a retired one none.
  const paused = de.status === "paused" || de.status === "not_deployed";
  const on = writer && de.status !== "retired" && !paused;
  const [opened, setOpened] = useState(!lazy);
  const load = on && opened;
  const all = useQuery({ ...channelsQuery(api, co), enabled: load, retry: false });
  const mine = useDeChannels(api, co, de.membership_id, load);
  const refresh = useRefresh(co);
  const add = useMutation({
    mutationFn: (id: string) =>
      api.client.channels.addMember({
        params: { co, id },
        body: { membership_id: de.membership_id },
      }),
    onSuccess: (_r, id) => {
      void refresh(id);
      const c = all.data?.data.find((x) => x.id === id);
      toast.add({
        title: `Added to ${c ? label(c) : "the channel"}`,
        type: "success",
      });
    },
    onError: (e) =>
      toast.add({
        title: "Could not add to the channel",
        description: problemText(e, e.message),
        type: "error",
      }),
  });
  if (writer && paused)
    return (
      <span className="inline-flex items-center gap-1">
        <Button type="button" variant="secondary" size="xs" disabled>
          <PlusIcon data-icon="inline-start" />
          Add to channel
        </Button>
        <InfoTip label="Why Add to channel is off">
          {de.status === "not_deployed"
            ? "Not deployed: deploy this digital employee to add it to a channel."
            : "Paused: resume this digital employee to add it to a channel."}
        </InfoTip>
      </span>
    );
  if (!on) return null;
  const inIt = new Set((mine.rows ?? []).map((c) => c.id));
  // Only channels where the caller may add members (`can_invite`, the API's own rule).
  const items = (all.data?.data ?? [])
    .filter((c) => c.can_invite)
    .filter((c) => !c.dm_key && !c.archived_at && !c.is_agent_room && !inIt.has(c.id))
    .filter((c) => c.kind !== "dm" && c.kind !== "group_dm")
    .sort((a, b) => a.name.localeCompare(b.name))
    .map((c) => ({
      id: c.id,
      text: c.name,
      render: (
        <>
          {c.is_private ? (
            <LockIcon aria-hidden="true" className="size-3.5 shrink-0 text-faint" />
          ) : (
            <HashIcon aria-hidden="true" className="size-3.5 shrink-0 text-faint" />
          )}
          <span className="min-w-0 truncate">{c.name}</span>
        </>
      ),
    }));
  return (
    <SearchPicker
      label="Add to channel"
      variant="secondary"
      size="xs"
      placeholder="Search channels"
      empty="No other channel you can add members to."
      disabled={add.isPending}
      // Offered only once both reads are whole: an unread membership is not "in none".
      status={
        all.isError || mine.failed ? "error" : all.isSuccess && mine.rows ? "ready" : "loading"
      }
      onRetry={() => {
        if (all.isError) void all.refetch();
        if (mine.failed) mine.retry();
      }}
      items={items}
      onPick={(id) => add.mutate(id)}
      onOpenChange={(o) => {
        if (o) setOpened(true);
      }}
    >
      <PlusIcon data-icon="inline-start" />
      Add to channel
    </SearchPicker>
  );
}

/** DE › Overview: the channels the DE is in. */
export function DeChannelsSection({ api, company, de }: Props) {
  const chat = useFeature("chat");
  if (!chat.enabled)
    return (
      <SettingsSection icon={MessagesSquareIcon} title="Channels">
        <p className="m-0 text-sm text-muted-foreground" data-slot="de-channels-off">
          Chat is off for this company.
        </p>
      </SettingsSection>
    );
  return <DeChannelsList api={api} company={company} de={de} />;
}

function DeChannelsList({ api, company, de }: Props) {
  const co = company.id;
  const mine = useDeChannels(api, co, de.membership_id, true);
  const rows = [...(mine.rows ?? [])].sort((a, b) => a.name.localeCompare(b.name));
  const rules = useQueries({
    queries: rows.map((c) => ({ ...rulesQuery(api, co, c.id), retry: false })),
  });
  const job = useQuery({ ...jobQuery(api, co, de.membership_id) });
  const home = job.data?.home_channel_id ?? null;
  const writer = useChatWriter(company);
  // WF-FIX (2026-10-26): per channel — the row's `can_manage` (the API's own rule: the space's
  // owners and the channel's admins), never a DM (its members are its key).
  const canRemove = (c: SidebarChannel) =>
    writer && c.can_manage && c.kind !== "dm" && c.kind !== "group_dm";
  const refresh = useRefresh(co);
  const remove = useMutation({
    mutationFn: (id: string) =>
      api.client.channels.removeMember({
        params: { co, id, membership_id: de.membership_id },
      }),
    onSuccess: (_r, id) => {
      void refresh(id);
      const c = rows.find((x) => x.id === id);
      toast.add({ title: `Removed from ${c ? label(c) : "the channel"}`, type: "success" });
    },
    onError: (e) =>
      toast.add({
        title: "Could not remove from the channel",
        description: problemText(e, e.message),
        type: "error",
      }),
  });
  return (
    <SettingsSection
      icon={MessagesSquareIcon}
      title="Channels"
      actions={<AddToChannelButton api={api} company={company} de={de} />}
    >
      {mine.failed ? (
        <p role="alert" className="m-0 flex items-center gap-2 text-sm text-muted-foreground">
          Could not load the channels.
          <Button type="button" variant="secondary" size="xs" onClick={mine.retry}>
            Retry
          </Button>
        </p>
      ) : !mine.rows ? (
        <p
          role="status"
          aria-label="Loading channels"
          className="m-0 text-sm text-muted-foreground"
        >
          Loading…
        </p>
      ) : !rows.length ? (
        <p className="m-0 text-sm text-muted-foreground" data-slot="de-channels-empty">
          Not in any channel yet
        </p>
      ) : (
        <ul aria-label="Channels" className="m-0 flex list-none flex-col p-0">
          {rows.map((c, i) => {
            const wake = rules[i]?.data?.wake;
            return (
              <li
                key={c.id}
                data-de-channel={c.id}
                className="group/channel flex items-center gap-2 py-1 text-sm"
              >
                {c.is_private ? (
                  <LockIcon aria-label="Private" className="size-3.5 shrink-0 text-faint" />
                ) : (
                  <HashIcon aria-hidden="true" className="size-3.5 shrink-0 text-faint" />
                )}
                <Link
                  to="/c/$slug/chat/$channelId"
                  params={{ slug: company.slug, channelId: c.id }}
                  search={keepFocus as never}
                  className="min-w-0 truncate font-medium text-foreground hover:underline"
                >
                  {c.name}
                </Link>
                {c.id === home ? <Badge variant="info">Home</Badge> : null}
                {c.archived_at ? <Badge variant="outline">Archived</Badge> : null}
                {wake ? (
                  <span
                    data-slot="wake-rule"
                    title="When agents answer in this channel"
                    className="rounded-full border border-border px-1.5 text-[11px] leading-4 text-muted-foreground"
                  >
                    {WAKE_CHIP[wake]}
                  </span>
                ) : null}
                <span className="flex-1" />
                {canRemove(c) ? (
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon-xs"
                    className="text-faint opacity-0 hover:bg-danger-soft hover:text-danger-text group-hover/channel:opacity-100 focus-visible:opacity-100 pointer-coarse:opacity-100"
                    aria-label={`Remove from #${c.name}`}
                    title="Remove from channel"
                    disabled={remove.isPending}
                    onClick={() => remove.mutate(c.id)}
                  >
                    <Trash2Icon />
                  </Button>
                ) : null}
              </li>
            );
          })}
        </ul>
      )}
    </SettingsSection>
  );
}
